import { type JSX, useState } from 'react';

import { cloneTenant, deleteFleet, type MeTenant } from '../api.js';
import { confirmDialog, promptDialog } from './dialogs.js';

export interface FleetsListProps {
  /** Tenants the signed-in customer owns. Populated by GET /me. */
  tenants: MeTenant[];
  onCustomize: (input: {
    tenantId: string;
    /** null for a non-owner admin — the editor authorizes via the cookie. */
    editToken: string | null;
    launcherUrl: string;
    role: MeTenant['role'];
  }) => void;
  /**
   * Fired after a successful duplicate. The parent should re-fetch /me
   * (so the new tenant shows up in the list) and typically navigate the
   * user straight into the new tenant's editor.
   */
  onDuplicated?: (input: {
    tenantId: string;
    editToken: string;
    launcherUrl: string;
  }) => void;
  /**
   * Fired after a successful delete. The parent should re-fetch /me so the
   * removed fleet drops out of the list.
   */
  onDeleted?: (tenantId: string) => void;
}

/**
 * Lists the tenants the signed-in customer owns. Data comes from the
 * Portal's /me call — no localStorage. Each row links back to the
 * launcher and offers a Customize CTA that opens the editor.
 *
 * A previously-stored editToken is required to call PUT /tenant/configs
 * — the server-side flow ensures every tenant the /me query returns
 * has one, but we guard for null just in case (staff-published configs).
 */
export function FleetsList({
  tenants,
  onCustomize,
  onDuplicated,
  onDeleted,
}: FleetsListProps): JSX.Element {
  // Per-row lock so a double-click doesn't fire two ops. Single
  // string-or-null is fine — we never mutate more than one fleet at a time;
  // `busyKind` just drives the button label of whichever op is running.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<'duplicate' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function duplicate(t: MeTenant): Promise<void> {
    // Cloning is the 'edit' capability — owners + admins, via the session
    // cookie (editToken is null for humans now).
    if (t.role === 'viewer') return;
    const next = await promptDialog({
      title: 'Duplicate fleet',
      message: `Duplicate "${t.deploymentName}" — name the new fleet:`,
      defaultValue: `${t.deploymentName} (copy)`,
      confirmLabel: 'Duplicate',
    });
    if (next === null) return;
    const deploymentName = next.trim();
    if (deploymentName.length === 0) return;

    setBusyId(t.tenantId);
    setBusyKind('duplicate');
    setError(null);
    const res = await cloneTenant({
      sourceTenantId: t.tenantId,
      editToken: t.editToken,
      deploymentName,
    });
    setBusyId(null);
    setBusyKind(null);
    if (res.kind === 'ok') {
      onDuplicated?.({
        tenantId: res.tenantId,
        editToken: res.editToken,
        launcherUrl: res.launcherUrl,
      });
      return;
    }
    setError(
      res.kind === 'unauthorized'
        ? 'Edit token rejected — sign back in and try again.'
        : res.kind === 'not-found'
          ? 'Source tenant not found.'
          : res.kind === 'invalid'
            ? res.message
            : res.kind === 'network'
              ? `Network: ${res.message}`
              : `Server returned ${String(res.status)}.`,
    );
  }

  async function remove(t: MeTenant): Promise<void> {
    // Deleting a fleet is the 'delete' capability — owner only, via the cookie.
    if (t.role !== 'owner') return;
    // Type-the-fleet-name guard: deleting a fleet is irreversible and
    // cascades all its data, so require the exact name before proceeding.
    const confirmed = await confirmDialog({
      title: 'Delete fleet',
      message: `Permanently delete "${t.deploymentName}"? This removes its config, licenses, devices, broadcasts, and telemetry. This cannot be undone.`,
      destructive: true,
      confirmLabel: 'Delete fleet',
      requireText: t.deploymentName,
    });
    if (!confirmed) return;

    setBusyId(t.tenantId);
    setBusyKind('delete');
    setError(null);
    const res = await deleteFleet({ tenantId: t.tenantId, editToken: t.editToken });
    setBusyId(null);
    setBusyKind(null);
    if (res.kind === 'ok') {
      onDeleted?.(t.tenantId);
      return;
    }
    setError(
      res.kind === 'unauthorized'
        ? 'Edit token rejected — sign back in and try again.'
        : res.kind === 'network'
          ? `Network: ${res.message}`
          : `Server returned ${String(res.status)}.`,
    );
  }

  if (tenants.length === 0) {
    return (
      <section className="details fleets-empty" data-testid="fleets-empty">
        <p className="details__hint">
          No fleets on this account yet. Head to <strong>Start free</strong> to
          launch one — it'll attach to this signed-in email automatically.
        </p>
      </section>
    );
  }

  return (
    <section className="fleets" data-testid="fleets-list">
      {error !== null && (
        <p className="details__bundle-err" role="alert" data-testid="fleets-error">
          {error}
        </p>
      )}
      <ul className="fleets__list">
        {tenants.map((t) => (
          <li key={t.tenantId} className="fleets__row" data-testid={`fleet-${t.tenantId}`}>
            <div className="fleets__row-main">
              <span className="fleets__row-name">
                <span className="fleets__row-name-text">{t.deploymentName}</span>
                <span className={`fleets__row-tier fleets__row-tier--${t.tier}`}>
                  {t.tier}
                </span>
                {t.role !== 'owner' && (
                  <span
                    className={`fleets__row-role fleets__row-role--${t.role}`}
                    data-testid={`fleet-role-${t.tenantId}`}
                  >
                    {t.role}
                  </span>
                )}
              </span>
              <a
                className="fleets__row-link"
                href={t.launcherUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.launcherUrl}
              </a>
            </div>
            <div className="fleets__row-actions">
              {(() => {
                // Owners + admins open the editor to make changes (owner via
                // token, admin via cookie); viewers open it read-only. Only a
                // staff-published owner config (no token, no membership row to
                // fall back on) can't be opened.
                const canOpen =
                  t.role === 'admin' || t.role === 'viewer' || t.editToken !== null;
                const isViewer = t.role === 'viewer';
                return (
                  <button
                    type="button"
                    className="details__bundle fleets__btn"
                    onClick={() => {
                      if (!canOpen) return;
                      onCustomize({
                        tenantId: t.tenantId,
                        editToken: t.editToken,
                        launcherUrl: t.launcherUrl,
                        role: t.role,
                      });
                    }}
                    disabled={!canOpen}
                    title={
                      canOpen
                        ? isViewer
                          ? 'Open read-only — you have viewer access'
                          : undefined
                        : 'Staff-published config — contact support to edit'
                    }
                    data-testid={`fleet-customize-${t.tenantId}`}
                  >
                    {isViewer ? 'View' : 'Customize'}
                  </button>
                );
              })()}
              <a
                className="details__download fleets__btn"
                href={t.launcherUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </a>
              {t.role !== 'viewer' && (
                <button
                  type="button"
                  className="details__again fleets__btn"
                  onClick={() => {
                    void duplicate(t);
                  }}
                  disabled={busyId !== null}
                  title="Create a new fleet using this one as a template"
                  data-testid={`fleet-duplicate-${t.tenantId}`}
                >
                  {busyId === t.tenantId && busyKind === 'duplicate' ? 'Duplicating…' : '⎘ Duplicate'}
                </button>
              )}
              {t.role === 'owner' && (
                <button
                  type="button"
                  className="details__bundle fleets__btn fleets__btn--danger"
                  onClick={() => {
                    void remove(t);
                  }}
                  disabled={busyId !== null}
                  title="Permanently delete this fleet"
                  data-testid={`fleet-delete-${t.tenantId}`}
                >
                  {busyId === t.tenantId && busyKind === 'delete' ? 'Deleting…' : '🗑 Delete'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
